// #region imports

// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

// --- 3rd Party
import _ from 'lodash';

// --- HS Code
import { NotificationModel, Constants, NotificationTypes, SavedReportTypes } from '../../../models';
import { ReportsBaseService } from '../reporting/reports-base.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export abstract class UserNotificationBaseService {
  protected _notificationList: NotificationModel[];
  protected _notifications$: BehaviorSubject<NotificationModel[]>;
  get notifications$(): Observable<NotificationModel[]> {
    return this._notifications$.asObservable();
  }

  protected _currentNotification$: BehaviorSubject<NotificationModel | null>;
  get currentNotification$(): Observable<NotificationModel | null> {
    return this._currentNotification$;
  }

  get numberOfNewNotifications$(): Observable<number> {
    return this._notifications$.pipe(
      map((notifications) => {
        if (Array.isArray(notifications)) {
          return notifications.filter(n => !n.isRead).length;
        }

        return 0;
      })
    );
  }

  get hasNotifications$(): Observable<boolean> {
    return this._notifications$.pipe(
      map((notifications) => {
        if (Array.isArray(notifications)) {
          return ((notifications.filter(n => !n.isRead).length) > 0);
        }

        return false;
      })
    )
  }

  constructor(protected reportsService: ReportsBaseService) {
    this._notificationList = new Array<NotificationModel>();
    this._notifications$ = new BehaviorSubject<NotificationModel[]>(new Array<NotificationModel>());
    this._currentNotification$ = new BehaviorSubject<NotificationModel | null>(null);
  }

  abstract connect(): void;
  abstract signOut(): void;
  abstract view(notification: NotificationModel): void;
  abstract remove(notification: NotificationModel): void;

  protected _processNotification(notification: NotificationModel): void {
    const noti = _.cloneDeep(notification);
    this._currentNotification$.next(noti);

    if (notification.type < NotificationTypes.trackMyJourneyDownloadReady) {
      this._notificationList.unshift(notification);
      this._sortNotifications();

      const newList = this._notificationList.slice(0, Constants.MAXIMUM_NUMBER_OF_DISPLAY_NOTIFICATIONS);
      this._notifications$.next(newList);
    } else {
      if (notification.type === NotificationTypes.trackMyJourneyDownloadReady) {
        this._notificationList.unshift(notification);
        this._sortNotifications();

        const newList = this._notificationList.slice(0, Constants.MAXIMUM_NUMBER_OF_DISPLAY_NOTIFICATIONS);
        this._notifications$.next(newList);

        this.reportsService.getReportPdf(notification.identifier, SavedReportTypes.tmj);
      }
    }
  }

  private _sortNotifications() {
    this._notificationList = this._notificationList.sort((a, b) => {
      if (a.dateOfNotification > b.dateOfNotification) {
        return -1;
      } else if (a.dateOfNotification < b.dateOfNotification) {
        return 1;
      }
      return 0;
    });
  }
}
